import React, { useMemo, useState } from 'react';

import { link } from '@aline/routes';
import { Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid-premium';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlineButton,
  AlineCheckbox,
  ListEmpty,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { LabelsCell } from 'src/components/admin';
import { useAccessGuard } from 'src/components/auth';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { GroupIcon } from 'src/components/icons/GroupIcon';
import { WhitePlusIcon } from 'src/components/icons/WhitePlusIcon';
import { Apollo } from 'src/gql/generated';
import { overflowMenuCSSId } from 'src/helpers';
import {
  createDataGridPremiumFooterlessPaperStyles,
  useCreateGroup,
  useGetGroups,
} from 'src/hooks';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  emptyIcon: {
    height: 32,
    width: 32,
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      [`&:hover #${overflowMenuCSSId}`]: {
        visibility: 'visible',
      },
    },
    flexGrow: 1,
    minHeight: 0,
  },
}));

const useGroupNameCellStyles = makeStyles()(theme => ({
  cell: {
    width: '100%',
  },
  nameLink: {
    display: 'block',
    minWidth: 0,
    textDecoration: 'none',
  },
  nameText: {
    ...createClampStyles(2),
    color: theme.appColors.iconsType,
  },
}));

function GroupNameCell({ row, value }: GridRenderCellParams<GroupRow>): JSX.Element {
  const { classes } = useGroupNameCellStyles();

  return (
    <div className={classes.cell}>
      <Link
        className={classes.nameLink}
        to={link.AdminGroupDetail({ groupId: row.id })}
        onClick={e => e.stopPropagation()}
      >
        <Typography className={classes.nameText} variant="body2">
          {value || 'Untitled'}
        </Typography>
      </Link>
    </div>
  );
}

export interface GroupRow extends Pick<Apollo.Group, 'id' | 'name' | '__typename' | 'userCount'> {
  labels: Pick<Apollo.Label, 'id' | 'labelText' | '__typename'>[];
}
type GroupGridDef = GridColDef<GroupRow>;

export function GroupsList(): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { groups, loading } = useGetGroups({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name' satisfies keyof GroupRow, sort: 'asc' },
  ]);
  const columnDefs: GroupGridDef[] = useMemo(() => {
    return [
      {
        field: 'name',
        flex: 0,
        headerName: 'Name',
        minWidth: 200,
        renderCell: GroupNameCell,
        type: 'string',
      },
      {
        field: 'userCount',
        flex: 0,
        headerName: '# of Members',
        minWidth: 200,
      },
      {
        field: 'labels',
        flex: 1,
        headerName: 'Permissions by Label',
        renderCell: (params: GridRenderCellParams<GroupRow, GroupRow['labels']>) => (
          <LabelsCell {...params} />
        ),
        sortable: false,
      },
    ];
  }, []);

  return (
    <div className={classes.listContainer}>
      <DataGridPremium<GroupRow>
        disableColumnFilter
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        disableRowSelectionOnClick
        hideFooter
        showToolbar
        className={classes.xGrid}
        columns={columnDefs}
        getRowHeight={() => 'auto'}
        loading={loading}
        rows={groups}
        slotProps={{
          loadingOverlay: {
            noRowsVariant: 'linear-progress',
            variant: 'linear-progress',
          },
        }}
        slots={{
          baseCheckbox: AlineCheckbox,
          baseTooltip: GridAlineTooltip,
          noRowsOverlay: GroupsListEmpty,
          toolbar: GroupsListToolbar,
        }}
        sortModel={sortModel}
        sx={createDataGridPremiumFooterlessPaperStyles}
        onRowClick={row => navigate(link.AdminGroupDetail({ groupId: String(row.id) }))}
        onSortModelChange={setSortModel}
      />
    </div>
  );
}

function GroupsListEmpty(): JSX.Element {
  const { classes } = useStyles();

  return (
    <ListEmpty
      icon={<GroupIcon className={classes.emptyIcon} fill="#9CA7AB" />}
      subtitle="Click new to create a group"
      title="No groups"
    />
  );
}

function GroupsListToolbar(): JSX.Element {
  const navigate = useNavigate();
  const { classes } = useListToolbarStyles();
  const { createGroup, loading } = useCreateGroup();
  const handleClickNewGroup = useAccessGuard({
    callback: async () => {
      const { data } = await createGroup({
        variables: { input: { props: { name: '' }, userIds: [] } },
      });
      if (!data) return;

      navigate(link.AdminGroupDetail({ groupId: data.createGroup.id }));
    },
    minRole: Apollo.OrgRole.Admin,
    minTier: Apollo.SubscriptionTier.Enterprise,
  });

  return (
    <div className={classes.toolbar}>
      <Spacer />
      <AlineButton
        color="primary"
        endIcon={<WhitePlusIcon />}
        loading={loading}
        variant="contained"
        onClick={handleClickNewGroup}
      >
        New Group
      </AlineButton>
    </div>
  );
}
